import React, { useEffect, useRef, useState } from 'react';
import { Link, Redirect, useHistory } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../hooks/useConfig';
import { sanitizeLoginErrorMessage } from '../utils/authLogin';
import { isValidEmail, isValidMobileNumber } from '../utils/contactValidation';
import { submitLandingEnquiry } from '../services/landingEnquiry';
import { HeroWallpaper } from '../components/landing/HeroWallpaper';
import { ProductExplorer } from '../components/landing/ProductExplorer';
import { LiveCommandCenter } from '../components/landing/LiveCommandCenter';
import { AnalyticsShowcase } from '../components/landing/AnalyticsShowcase';

const PLATFORM_TITLE = 'SkoolFix';
const PLATFORM_HEADLINE = 'Run the campus — not just the records';
const PLATFORM_SUPPORT =
  'Gate pickup, WhatsApp parents own, verifiable certificates, fee-gated admit cards, live bus, and your school website — on one stack built for Indian K–12.';
const PLATFORM_TAGLINE = 'The campus OS for modern Indian schools';

function BrandWordmark({ className = '' }: { className?: string }) {
  return (
    <span className={`brand-wordmark ${className}`.trim()}>
      <span className="brand-wordmark__skool">Skool</span>
      <span className="brand-wordmark__fix">Fix</span>
    </span>
  );
}

type ContactFormState = {
  name: string;
  email: string;
  phone: string;
  schoolName: string;
  city: string;
  studentCount: string;
  message: string;
};

const EMPTY_CONTACT_FORM: ContactFormState = {
  name: '',
  email: '',
  phone: '',
  schoolName: '',
  city: '',
  studentCount: '',
  message: '',
};

const FEATURES: Array<{ title: string; body: string }> = [
  {
    title: 'WhatsApp the school owns',
    body: 'Meta Business onboarding, bulk send by class or sheet, inbox, delivery logs, and cost tracking — an operational parent line, not “SMS someday.”',
  },
  {
    title: 'Guardian pickup at the gate',
    body: 'Search by phone or admission number, match authorized guardians, issue daily PIN/QR passes, and log every handover — security that protects children.',
  },
  {
    title: 'QR-verified documents',
    body: 'Certificates, admit cards, and invoices with authenticity codes. Anyone can scan and confirm real vs forged — school-branded PDFs parents and other schools can trust.',
  },
  {
    title: 'Fee-gated admit cards',
    body: 'Bulk admit-card campaigns that stay blocked until fees clear and exam rules are met — no more print first, chase later.',
  },
  {
    title: 'Live bus for parents',
    body: 'Near real-time location on the parent portal so families stop calling the office to ask if the bus is late.',
  },
  {
    title: 'ERP + school website, one tenant',
    body: 'Campus ops and a public school site (CMS, gallery, alerts) on the same subscription — sell ERP, website, or both.',
  },
  {
    title: 'AI question bank for teachers',
    body: 'Generate questions with AI, save them to a school-owned bank, and reuse across exams — not one-off ChatGPT copy-paste.',
  },
  {
    title: 'Campus ops with depth',
    body: 'Admissions CRM, procurement (PO → GRN → payment), fee waivers, teaching analytics, attendance, hostel, library, and multi-campus SaaS — plus the records layer every school needs.',
  },
];

const STEPS: Array<{ step: string; title: string; body: string }> = [
  {
    step: '01',
    title: 'Tell us about your school',
    body: 'Share campus size, branches, and what you need live first — gate, WhatsApp, fees, academics, or the full stack.',
  },
  {
    step: '02',
    title: 'We configure your campus',
    body: 'Classes, fee plans, roles, modules, and branding land ready so staff are not starting from a blank slate.',
  },
  {
    step: '03',
    title: 'Go live with your team',
    body: 'Teachers mark attendance, accounts collect fees, security runs the gate, and parents stay informed from day one.',
  },
];

const ROLES: Array<{ title: string; body: string }> = [
  {
    title: 'School admin',
    body: 'Students, fees, exams, admissions, procurement, and campus-wide oversight from one calm place.',
  },
  {
    title: 'Teachers',
    body: 'Attendance, homework, marks, AI question banks, and a clear view of how their classes are progressing.',
  },
  {
    title: 'Parents & students',
    body: 'Fees, progress, pickup PIN/QR, live bus, and linked children — without juggling multiple apps.',
  },
  {
    title: 'Security & gate staff',
    body: 'Verify guardians, scan pickup passes, and keep a clean handover log at dismissal.',
  },
  {
    title: 'Platform operators',
    body: 'Onboard schools, toggle ERP and website, and keep every campus healthy from one console.',
  },
];

function Reveal({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.matches) {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={ref} className={`landing-reveal${visible ? ' is-visible' : ''} ${className}`.trim()}>
      {children}
    </div>
  );
}

export function LandingPage() {
  const { login, isAuthenticated, loading } = useAuth();
  const { loading: configLoading } = useConfig();
  const history = useHistory();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const [contactForm, setContactForm] = useState<ContactFormState>(EMPTY_CONTACT_FORM);
  const [contactError, setContactError] = useState<string | null>(null);
  const [contactSent, setContactSent] = useState(false);
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [navScrolled, setNavScrolled] = useState(false);

  useEffect(() => {
    if (!loginOpen) return;
    const t = window.setTimeout(() => emailInputRef.current?.focus(), 40);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLoginOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [loginOpen]);

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, '');

    // This page mounts late (after config/auth), so the hero wallpaper and the
    // reveal sections are still settling on the first frame. Re-assert the
    // landing position once layout has caught up, or the visitor starts mid-page.
    const settle = () => {
      if (hash && hash !== 'top') {
        document.getElementById(hash)?.scrollIntoView({ behavior: 'auto', block: 'start' });
      } else {
        window.scrollTo(0, 0);
      }
    };

    settle();
    const raf = window.requestAnimationFrame(settle);
    window.addEventListener('load', settle);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('load', settle);
    };
  }, []);

  if (!loading && !configLoading && isAuthenticated) {
    return <Redirect to="/" />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      history.replace('/');
    } catch (err) {
      setError(sanitizeLoginErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const openLogin = () => {
    setError(null);
    setLoginOpen(true);
  };

  const closeLogin = () => {
    if (submitting) return;
    setLoginOpen(false);
  };

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const updateContactField = (field: keyof ContactFormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setContactSent(false);
    setContactError(null);
    setContactForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (contactSubmitting) return;

    setContactError(null);
    setContactSent(false);

    const name = contactForm.name.trim();
    const email = contactForm.email.trim();
    const phone = contactForm.phone.trim();
    const schoolName = contactForm.schoolName.trim();
    const city = contactForm.city.trim();
    const studentCount = contactForm.studentCount.trim();
    const message = contactForm.message.trim();

    if (!name) {
      setContactError('Please enter your name.');
      return;
    }
    if (!isValidEmail(email)) {
      setContactError('Please enter a valid email address.');
      return;
    }
    if (!isValidMobileNumber(phone)) {
      setContactError('Please enter a valid phone number (10–15 digits).');
      return;
    }

    setContactSubmitting(true);
    try {
      await submitLandingEnquiry({
        name,
        email,
        phone,
        schoolName: schoolName || undefined,
        city: city || undefined,
        studentCount: studentCount || undefined,
        message: message || undefined,
      });
      setContactForm(EMPTY_CONTACT_FORM);
      setContactSent(true);
    } catch (err) {
      setContactError(err instanceof Error ? err.message : 'Could not send enquiry. Please try again.');
    } finally {
      setContactSubmitting(false);
    }
  };

  return (
    <div className="landing-page">
      <header className={`landing-nav${navScrolled ? ' landing-nav--scrolled' : ''}`}>
        <a
          href="#top"
          className="landing-nav__brand"
          aria-label={PLATFORM_TITLE}
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        >
          <BrandWordmark />
        </a>
        <nav className="landing-nav__links" aria-label="Page sections">
          <button type="button" onClick={() => scrollTo('features')}>
            Features
          </button>
          <button type="button" onClick={() => scrollTo('analytics')}>
            Analytics
          </button>
          <button type="button" onClick={() => scrollTo('insights')}>
            Live demo
          </button>
          <button type="button" onClick={() => scrollTo('product')}>
            Product
          </button>
          <button type="button" onClick={() => scrollTo('contact')}>
            Contact
          </button>
          <Link to="/careers">Careers</Link>
        </nav>
        <div className="landing-nav__actions">
          <button type="button" className="landing-nav__login" onClick={openLogin}>
            Login
          </button>
          <button type="button" className="landing-nav__demo" onClick={() => scrollTo('contact')}>
            Book demo
          </button>
        </div>
      </header>

      <section id="top" className="landing-hero" aria-label="SkoolFix home">
        <HeroWallpaper />
        <div className="landing-hero__inner landing-hero__inner--solo">
          <div className="landing-hero__brand">
            <h1 className="landing-hero__title">
              <BrandWordmark className="brand-wordmark--hero" />
            </h1>
            <span className="landing-hero__rule" aria-hidden />
            <p className="landing-hero__headline">{PLATFORM_HEADLINE}</p>
            <p className="landing-hero__support">{PLATFORM_SUPPORT}</p>
            <div className="landing-hero__ctas">
              <button type="button" className="landing-hero__cta landing-hero__cta--primary" onClick={() => scrollTo('contact')}>
                Book a free demo
              </button>
              <button
                type="button"
                className="landing-hero__cta landing-hero__cta--ghost"
                onClick={() => scrollTo('analytics')}
              >
                Explore analytics
              </button>
            </div>
          </div>
        </div>
        <div className="landing-hero__scroll" aria-hidden>
          <span />
          <em>Scroll</em>
        </div>
      </section>

      <section id="features" className="landing-section landing-section--features">
        <div className="landing-section__inner">
          <Reveal className="landing-section__intro">
            <span className="landing-section__mark" aria-hidden />
            <h2 className="landing-section__title">What other ERPs leave on the table</h2>
            <p className="landing-section__lead">
              Students, fees, and attendance are table stakes. SkoolFix wins at the gate, WhatsApp cost, QR verify,
              fee-gated exams, live bus — and your school website — on one stack.
            </p>
          </Reveal>
          <div className="landing-features">
            {FEATURES.map((feature, i) => (
              <Reveal key={feature.title} className={`landing-features__item landing-features__item--d${i % 3}`}>
                <span className="landing-features__index" aria-hidden>
                  {String(i + 1).padStart(2, '0')}
                </span>
                <h3 className="landing-features__title">{feature.title}</h3>
                <p className="landing-features__body">{feature.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="analytics" className="landing-section landing-section--analytics">
        <div className="landing-section__inner">
          <Reveal className="landing-section__intro">
            <span className="landing-section__mark" aria-hidden />
            <h2 className="landing-section__title">Teacher &amp; student insights principals trust</h2>
            <p className="landing-section__lead">
              Spot teaching outcomes and student progress in one calm view — try the sample below. Deeper audits
              stay on your live school portal.
            </p>
          </Reveal>
          <AnalyticsShowcase />
        </div>
      </section>

      <section id="how" className="landing-section landing-section--how">
        <div className="landing-section__inner">
          <Reveal className="landing-section__intro">
            <span className="landing-section__mark" aria-hidden />
            <h2 className="landing-section__title">Live in three clear steps</h2>
            <p className="landing-section__lead">
              No six-month ERP project. We get your campus structured, then your team takes the wheel.
            </p>
          </Reveal>
          <ol className="landing-steps">
            {STEPS.map((item) => (
              <li key={item.step} className="landing-steps__item">
                <Reveal>
                  <span className="landing-steps__num" aria-hidden>
                    {item.step}
                  </span>
                  <h3 className="landing-steps__title">{item.title}</h3>
                  <p className="landing-steps__body">{item.body}</p>
                </Reveal>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section id="insights" className="landing-section landing-section--insights">
        <div className="landing-section__inner">
          <Reveal className="landing-section__intro">
            <span className="landing-section__mark landing-section__mark--light" aria-hidden />
            <h2 className="landing-section__title">Live campus command center</h2>
            <p className="landing-section__lead">
              A calm overview of attendance, fees, exam readiness, and what needs attention — sample preview only;
              your real campus data stays private.
            </p>
          </Reveal>
          <LiveCommandCenter />
        </div>
      </section>

      <section id="product" className="landing-section landing-section--product">
        <div className="landing-section__inner">
          <Reveal className="landing-section__intro">
            <span className="landing-section__mark" aria-hidden />
            <h2 className="landing-section__title">What SkoolFix covers</h2>
            <p className="landing-section__lead">
              Browse admissions, fees, exams, gate security, WhatsApp, transport, website, and more — outcomes only;
              setup detail happens on a guided demo.
            </p>
          </Reveal>
          <ProductExplorer />
        </div>
      </section>

      <section id="roles" className="landing-section landing-section--roles">
        <div className="landing-section__inner">
          <Reveal className="landing-section__intro">
            <span className="landing-section__mark" aria-hidden />
            <h2 className="landing-section__title">Built for every role on campus</h2>
            <p className="landing-section__lead">
              One platform, role-based views — admin, teacher, parent, security, and platform operators each see the
              work that matters to them.
            </p>
          </Reveal>
          <div className="landing-roles">
            {ROLES.map((role) => (
              <Reveal key={role.title} className="landing-roles__item">
                <h3 className="landing-roles__title">{role.title}</h3>
                <p className="landing-roles__body">{role.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="landing-section landing-section--contact">
        <div className="landing-section__inner landing-section__inner--split">
          <Reveal className="landing-contact-copy">
            <span className="landing-section__mark landing-section__mark--light" aria-hidden />
            <h2 className="landing-section__title">Book a campus walkthrough</h2>
            <p className="landing-section__lead">
              Tell us about your campus — we will help you pilot SkoolFix on your subdomain and reply on email.
            </p>
            <ul className="landing-contact-points">
              <li>Live path: gate pickup → WhatsApp → QR verify → fee-gated admit cards → live bus → website</li>
              <li>Fees, academics, security, and parent updates in one place</li>
              <li>No spam — just a clear next step for your team</li>
            </ul>
            <ul className="landing-contact-direct" aria-label="Direct contact">
              <li>
                <span className="landing-contact-direct__label">Phone</span>
                <a href="tel:+918147495082">8147495082</a>
              </li>
              <li>
                <span className="landing-contact-direct__label">Email</span>
                <a href="mailto:vishalvikash93@gmail.com">vishalvikash93@gmail.com</a>
              </li>
            </ul>
          </Reveal>
          <Reveal>
            <form className="landing-contact-form" onSubmit={handleContactSubmit} noValidate>
              <div className="landing-contact-form__row">
                <div className="landing-contact-form__field">
                  <label htmlFor="contact-name">Name</label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    required
                    className="royal-input"
                    value={contactForm.name}
                    onChange={updateContactField('name')}
                  />
                </div>
                <div className="landing-contact-form__field">
                  <label htmlFor="contact-email">Email</label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className="royal-input"
                    value={contactForm.email}
                    onChange={updateContactField('email')}
                  />
                </div>
              </div>
              <div className="landing-contact-form__field">
                <label htmlFor="contact-phone">Phone number</label>
                <input
                  id="contact-phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  required
                  className="royal-input"
                  value={contactForm.phone}
                  onChange={updateContactField('phone')}
                />
              </div>
              <div className="landing-contact-form__row">
                <div className="landing-contact-form__field">
                  <label htmlFor="contact-school">
                    School name <span className="landing-contact-form__optional">optional</span>
                  </label>
                  <input
                    id="contact-school"
                    name="schoolName"
                    type="text"
                    autoComplete="organization"
                    className="royal-input"
                    value={contactForm.schoolName}
                    onChange={updateContactField('schoolName')}
                  />
                </div>
                <div className="landing-contact-form__field">
                  <label htmlFor="contact-city">
                    City <span className="landing-contact-form__optional">optional</span>
                  </label>
                  <input
                    id="contact-city"
                    name="city"
                    type="text"
                    autoComplete="address-level2"
                    className="royal-input"
                    value={contactForm.city}
                    onChange={updateContactField('city')}
                  />
                </div>
              </div>
              <div className="landing-contact-form__field">
                <label htmlFor="contact-students">
                  Approx. number of students <span className="landing-contact-form__optional">optional</span>
                </label>
                <input
                  id="contact-students"
                  name="studentCount"
                  type="text"
                  inputMode="numeric"
                  className="royal-input"
                  placeholder="e.g. 500"
                  value={contactForm.studentCount}
                  onChange={updateContactField('studentCount')}
                />
              </div>
              <div className="landing-contact-form__field">
                <label htmlFor="contact-message">
                  Message <span className="landing-contact-form__optional">optional</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows={4}
                  className="royal-input landing-contact-form__textarea"
                  placeholder="What would you like help with?"
                  value={contactForm.message}
                  onChange={updateContactField('message')}
                />
              </div>

              {contactError && (
                <p className="landing-contact-form__status landing-contact-form__status--error" role="alert">
                  {contactError}
                </p>
              )}
              {contactSent && !contactError && (
                <p className="landing-contact-form__status landing-contact-form__status--ok" role="status">
                  Thanks — your enquiry was sent. We will reply by email.
                </p>
              )}

              <button
                type="submit"
                className="royal-btn-primary landing-contact-form__submit"
                disabled={contactSubmitting}
              >
                {contactSubmitting ? 'Sending…' : 'Send enquiry'}
              </button>
            </form>
          </Reveal>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="landing-footer__inner">
          <div className="landing-footer__brand">
            <p className="landing-footer__name">
              <BrandWordmark className="brand-wordmark--footer" />
            </p>
            <p className="landing-footer__tag">{PLATFORM_TAGLINE}</p>
          </div>
          <nav className="landing-footer__links" aria-label="Footer">
            <button type="button" onClick={() => scrollTo('features')}>
              Features
            </button>
            <button type="button" onClick={() => scrollTo('analytics')}>
              Analytics
            </button>
            <button type="button" onClick={() => scrollTo('product')}>
              Product
            </button>
            <button type="button" onClick={() => scrollTo('contact')}>
              Contact
            </button>
            <Link to="/careers">Careers</Link>
            <button type="button" onClick={openLogin}>
              Login
            </button>
          </nav>
          <p className="landing-footer__copy">
            © {new Date().getFullYear()} {PLATFORM_TITLE}
          </p>
        </div>
      </footer>

      {loginOpen && (
        <div className="landing-login-modal" role="presentation">
          <button type="button" className="landing-login-modal__backdrop" aria-label="Close login" onClick={closeLogin} />
          <div
            className="landing-login-modal__panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="platform-admin-login-title"
          >
            <button type="button" className="landing-login-modal__close" onClick={closeLogin} aria-label="Close">
              ×
            </button>
            <form onSubmit={handleSubmit} className="landing-login-modal__form">
              <p className="landing-hero__login-eyebrow">Platform</p>
              <h2 id="platform-admin-login-title" className="landing-hero__login-title">
                Platform Admin
              </h2>
              <p className="landing-hero__login-hint">
                For SkoolFix operators. School staff sign in on their campus URL.
              </p>

              <div className="space-y-4 mt-5">
                <div>
                  <label htmlFor="landing-admin-email" className="block text-sm font-medium text-secondary-700 mb-1">
                    Email
                  </label>
                  <input
                    ref={emailInputRef}
                    id="landing-admin-email"
                    type="email"
                    autoComplete="username"
                    required
                    className="royal-input w-full px-3 py-2.5 text-sm"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label htmlFor="landing-admin-password" className="block text-sm font-medium text-secondary-700">
                      Password
                    </label>
                    <Link to="/forgot-password" className="text-xs text-primary-600 hover:text-primary-700 font-medium">
                      Forgot password?
                    </Link>
                  </div>
                  <input
                    id="landing-admin-password"
                    type="password"
                    autoComplete="current-password"
                    required
                    className="royal-input w-full px-3 py-2.5 text-sm"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </div>

              {error && (
                <p className="mt-4 text-sm text-red-600" role="alert">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="mt-6 w-full royal-btn-primary py-2.5 rounded-lg text-sm font-medium disabled:opacity-50"
              >
                {submitting ? 'Signing in…' : 'Sign in'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
